import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { bg as bgLocale, enUS } from "date-fns/locale";
import {
  Bookmark, Trash2, ChevronDown, ChevronUp, Sparkles, Activity, Loader2, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

/**
 * BhiSnapshotsTab — list of the user's last 10 saved Breast Intelligence
 * snapshots + a trend chart that overlays the BHI Metabolic Score over
 * time. Mounted as the new "snapshots" tab inside the main
 * /dashboard/health-intelligence page.
 */

export default function BhiSnapshotsTab({ isDark = false, bg = true }) {
  const { API } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: list } = await axios.get(`${API}/breast-intelligence/snapshots`);
      const lite = list?.snapshots || [];
      // Fetch full data for each to get metabolic score (needed for trend)
      const enriched = await Promise.all(
        lite.map(async (s) => {
          try {
            const { data: full } = await axios.get(`${API}/breast-intelligence/snapshots/${s.id}`);
            return {
              ...s,
              metabolic_score: full?.data?.metabolic?.total ?? null,
              metabolic_band:  full?.data?.metabolic?.band  ?? null,
              tissue_age:      full?.data?.tissue_age?.tissue_age ?? null,
              dil_avg:         full?.data?.signals?.dil_avg ?? null,
              nis_avg:         full?.data?.signals?.nis_avg ?? null,
              ai_summary:      full?.ai_summary ?? "",
            };
          } catch (_e) {
            return { ...s, metabolic_score: null };
          }
        })
      );
      setItems(enriched);
    } catch (_e) {
      toast.error(bg ? "Неуспех при зареждане на snapshots." : "Failed to load snapshots.");
    } finally {
      setLoading(false);
    }
  }, [API, bg]);

  const remove = async (id) => {
    if (!window.confirm(bg ? "Сигурни ли сте, че искате да изтриете този snapshot?" : "Delete this snapshot?")) return;
    try {
      await axios.delete(`${API}/breast-intelligence/snapshots/${id}`);
      toast.success(bg ? "Изтрит" : "Deleted");
      setItems((it) => it.filter((s) => s.id !== id));
    } catch (_e) {
      toast.error(bg ? "Грешка при изтриване." : "Delete failed.");
    }
  };

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-7 h-7 animate-spin mx-auto opacity-50" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={`p-8 rounded-2xl text-center ${isDark ? "bg-slate-900/60 border border-slate-800" : "bg-white border border-slate-200"}`} data-testid="bhi-snapshots-empty">
        <Bookmark className="w-10 h-10 mx-auto opacity-30 mb-3" />
        <h3 className="text-base font-bold mb-1">
          {bg ? "Все още няма BHI snapshots" : "No BHI snapshots yet"}
        </h3>
        <p className="text-sm opacity-70 max-w-md mx-auto">
          {bg
            ? "Отворете страницата \"Гръдна интелигентност\" и натиснете „Запази в Health Intelligence“ под AI обобщението, за да запазите моментна снимка на цялата си гръдна биология."
            : "Open the Breast Intelligence page and tap \"Save to Health Intelligence\" under the AI summary to capture a full breast biology snapshot."}
        </p>
      </div>
    );
  }

  // Items come newest-first; reverse for trend chart so oldest is left
  const chronological = [...items].reverse();
  const scores = chronological.filter((i) => i.metabolic_score != null);

  return (
    <div className="space-y-5" data-testid="bhi-snapshots-tab">
      {/* Trend chart */}
      {scores.length >= 2 && <TrendChart items={scores} isDark={isDark} bg={bg} />}

      {/* Snapshot list */}
      <div className="space-y-3">
        {items.map((s, idx) => {
          const isExpanded = expanded[s.id];
          const date = s.created_at ? parseISO(s.created_at) : null;
          const dateLabel = date
            ? format(date, "d MMM yyyy · HH:mm", { locale: bg ? bgLocale : enUS })
            : "";
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
              className={`rounded-xl p-4 ${
                isDark
                  ? "bg-slate-900/60 border border-slate-800"
                  : "bg-white border border-slate-200"
              }`}
              data-testid={`bhi-snapshot-${idx}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      isDark ? "bg-pink-500/20" : "bg-pink-100"
                    }`}>
                      <Sparkles className={`w-4 h-4 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold truncate" title={s.label}>{s.label}</div>
                      <div className="text-[11px] opacity-60">{dateLabel}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px]">
                    {s.metabolic_score != null && (
                      <span className="px-2 py-0.5 rounded-full bg-pink-500/10 text-pink-700 dark:text-pink-300 font-semibold">
                        {bg ? "Резултат" : "Score"}: <b>{s.metabolic_score}/100</b>
                      </span>
                    )}
                    {s.tissue_age != null && (
                      <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-700 dark:text-purple-300 font-semibold">
                        {bg ? "Възраст на тък." : "Tissue age"}: <b>{s.tissue_age}{bg ? " г." : "y"}</b>
                      </span>
                    )}
                    {s.dil_avg != null && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 font-semibold">
                        DIL: <b>{s.dil_avg.toFixed?.(1) ?? s.dil_avg}</b>
                      </span>
                    )}
                  </div>
                  {(isExpanded ? s.ai_summary : s.ai_summary_preview) && (
                    <p className="text-xs leading-relaxed mt-3 opacity-80 whitespace-pre-line">
                      {isExpanded ? s.ai_summary : `${s.ai_summary_preview}…`}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [s.id]: !e[s.id] }))}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5"
                    aria-label="toggle"
                    data-testid={`bhi-snapshot-toggle-${idx}`}
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => remove(s.id)}
                    className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-500/10 text-rose-500"
                    aria-label="delete"
                    data-testid={`bhi-snapshot-delete-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className={`p-3 rounded-lg text-[11px] opacity-60 italic ${isDark ? "bg-black/30" : "bg-slate-50"}`}>
        {bg
          ? "Снимките са образователни моментни снимки. Пазим последните 10 за всеки потребител."
          : "Snapshots are educational captures. We keep the latest 10 per user."}
      </div>
    </div>
  );
}

// ──────────────────────── Trend Chart ────────────────────────

function TrendChart({ items, isDark, bg }) {
  // Layout
  const W = 720, H = 240, PAD_L = 36, PAD_R = 18, PAD_T = 18, PAD_B = 36;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const scores = items.map((i) => i.metabolic_score);
  const min = 0; // BHI score is 0–100
  const max = 100;
  const n = items.length;

  const xAt = (i) => PAD_L + (n === 1 ? innerW / 2 : (i * innerW) / (n - 1));
  const yAt = (v) => PAD_T + innerH - ((v - min) * innerH) / (max - min);

  const pathD = items
    .map((it, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(it.metabolic_score).toFixed(1)}`)
    .join(" ");

  const areaD = `${pathD} L ${xAt(n - 1).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} L ${xAt(0).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`;

  const last = items[items.length - 1];
  const first = items[0];
  const delta = last.metabolic_score - first.metabolic_score;
  const deltaColor = delta >= 0 ? "#22C55E" : "#EF4444";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-2xl ${
        isDark
          ? "bg-gradient-to-br from-pink-500/10 via-purple-500/8 to-rose-500/10 border border-pink-500/25"
          : "bg-gradient-to-br from-pink-50 via-purple-50 to-rose-50 border border-pink-200"
      }`}
      data-testid="bhi-trend-chart"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className={`w-4 h-4 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
          <h3 className="text-sm font-bold">
            {bg ? "Времева линия — Breast Metabolic Score" : "Timeline — Breast Metabolic Score"}
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Activity className="w-3.5 h-3.5" />
          <span>
            {n} {bg ? "snapshot-а" : "snapshots"}
          </span>
          <span className="font-bold" style={{ color: deltaColor }}>
            {delta >= 0 ? "+" : ""}{delta} {bg ? "пункта" : "pts"}
          </span>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((g) => (
          <g key={g}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yAt(g)} y2={yAt(g)} stroke={isDark ? "#334155" : "#E2E8F0"} strokeDasharray="3 3" />
            <text x={PAD_L - 6} y={yAt(g) + 4} textAnchor="end" style={{ fontSize: 10, fill: isDark ? "#94A3B8" : "#64748B" }}>{g}</text>
          </g>
        ))}
        {/* Band shading (green ≥75, yellow 55-75, orange 35-55, red <35) */}
        <rect x={PAD_L} y={yAt(100)} width={innerW} height={yAt(75) - yAt(100)} fill="#22C55E" opacity="0.06" />
        <rect x={PAD_L} y={yAt(75)}  width={innerW} height={yAt(55) - yAt(75)}  fill="#EAB308" opacity="0.06" />
        <rect x={PAD_L} y={yAt(55)}  width={innerW} height={yAt(35) - yAt(55)}  fill="#F97316" opacity="0.06" />
        <rect x={PAD_L} y={yAt(35)}  width={innerW} height={yAt(0)  - yAt(35)}  fill="#EF4444" opacity="0.06" />

        {/* Area under curve */}
        <path d={areaD} fill="url(#trendFill)" opacity="0.45" />
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#EC4899" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#EC4899" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Line */}
        <path d={pathD} fill="none" stroke="#EC4899" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Dots */}
        {items.map((it, i) => (
          <g key={it.id}>
            <circle cx={xAt(i)} cy={yAt(it.metabolic_score)} r="5" fill="#EC4899" stroke="#fff" strokeWidth="2" />
            <title>{`${it.label} — ${it.metabolic_score}/100`}</title>
            {/* X-axis label (date only) */}
            {(i === 0 || i === n - 1 || n <= 6) && (
              <text
                x={xAt(i)}
                y={PAD_T + innerH + 18}
                textAnchor="middle"
                style={{ fontSize: 10, fill: isDark ? "#94A3B8" : "#64748B" }}
              >
                {it.created_at ? format(parseISO(it.created_at), "d MMM", { locale: bg ? bgLocale : enUS }) : ""}
              </text>
            )}
          </g>
        ))}
      </svg>

      <p className="text-[10px] opacity-50 italic mt-2">
        {bg
          ? "Изброени са до 10 снимки. Score 75+ е защитна зона; 35- е висок натиск. Образователно представяне."
          : "Up to 10 snapshots. Score ≥75 = protective; <35 = high burden. Educational only."}
      </p>
    </motion.div>
  );
}
